import { BaseModelWithFiles } from "@/models/base-with-files.model"
import type {
  IOutgoingInvoice,
  IOutgoingInvoiceIssuer,
  IOutgoingInvoiceReference
} from "@/interfaces/api/outgoing-invoice.interface"
import type { Moment } from "moment"
import { OutgoingInvoiceStatuses } from "@/enums/models/outgoing-invoice-statuses"
import {
  OutgoingInvoiceVatRates,
  outgoingInvoiceVatRateLabel
} from "@/enums/models/outgoing-invoice-vat-rates"
import type { FileRelation } from "@/models/file/file-relation.model"
import { ModelFactory } from "@/factories/model-factory"

/**
 * A sales invoice for one approval, or the credit invoice cancelling one (PNTS-3220, PNTS-3221). Everything on it
 * is fixed by the server; only its processing state moves, through socket updates.
 */
export class OutgoingInvoice extends BaseModelWithFiles implements IOutgoingInvoice {
  public static className = "OutgoingInvoice"
  public static verboseName = "Verkoopfactuur"

  number: string
  productionStatementApproval: string | null
  creditedInvoiceId: string | null
  /** The invoice this credit cancels, when it came nested. */
  creditedInvoice: OutgoingInvoice | null
  creditInvoiceId: string | null
  /** The credit invoice cancelling this one, when it came nested. */
  creditInvoice: OutgoingInvoice | null
  productionStatementApprovalSnapshot: string | null
  recipient: string
  recipientName: string
  recipientAddress: string[]
  recipientVatId: string
  recipientEmails: string
  date: Moment
  dueDate: Moment | null
  paymentTermDays: number | null
  references: IOutgoingInvoiceReference[]
  invoiceNote: string
  amountExclVat: string
  vatRate: OutgoingInvoiceVatRates
  vatAmount: string
  total: string
  gAccountPercentage: string | null
  gAccountAmount: string
  regularAmount: string
  issuer: IOutgoingInvoiceIssuer
  status: OutgoingInvoiceStatuses
  generatedAt: Moment | null
  sentAt: Moment | null
  failureDetail: string
  isCredited: boolean
  createdByName: string

  constructor(invoice: IOutgoingInvoice, managed = false) {
    super(invoice, managed)

    this.number = invoice.number
    this.productionStatementApproval = invoice.productionStatementApproval ?? null
    this.creditedInvoiceId = OutgoingInvoice.relationId(invoice.creditedInvoice)
    this.creditedInvoice = OutgoingInvoice.relationModel(invoice.creditedInvoice, managed)
    this.creditInvoiceId = OutgoingInvoice.relationId(invoice.creditInvoice)
    this.creditInvoice = OutgoingInvoice.relationModel(invoice.creditInvoice, managed)
    this.productionStatementApprovalSnapshot = invoice.productionStatementApprovalSnapshot ?? null
    this.recipient = invoice.recipient
    this.recipientName = invoice.recipientName
    this.recipientAddress = invoice.recipientAddress ?? []
    this.recipientVatId = invoice.recipientVatId ?? ""
    this.recipientEmails = invoice.recipientEmails ?? ""
    this.date = invoice.date
    this.dueDate = invoice.dueDate ?? null
    this.paymentTermDays = invoice.paymentTermDays ?? null
    this.references = invoice.references ?? []
    this.invoiceNote = invoice.invoiceNote ?? ""
    this.amountExclVat = invoice.amountExclVat
    this.vatRate = invoice.vatRate
    this.vatAmount = invoice.vatAmount
    this.total = invoice.total
    this.gAccountPercentage = invoice.gAccountPercentage ?? null
    this.gAccountAmount = invoice.gAccountAmount
    this.regularAmount = invoice.regularAmount
    this.issuer = invoice.issuer ?? {}
    this.status = invoice.status
    this.generatedAt = invoice.generatedAt ?? null
    this.sentAt = invoice.sentAt ?? null
    this.failureDetail = invoice.failureDetail ?? ""
    this.isCredited = invoice.isCredited ?? false
    this.createdByName = invoice.createdByName ?? ""
  }

  private static relationId(value: IOutgoingInvoice | string | null | undefined): string | null {
    if (!value) return null
    return typeof value === "string" ? value : (value.id ?? null)
  }

  private static relationModel(
    value: IOutgoingInvoice | string | null | undefined,
    managed: boolean
  ): OutgoingInvoice | null {
    return value && typeof value === "object"
      ? ModelFactory.childModel(OutgoingInvoice, value, managed)
      : null
  }

  public get isCredit(): boolean {
    return this.creditedInvoiceId !== null
  }

  /** The credit invoice cancelling this one, or the invoice this credit cancels: the other side. */
  public get counterpart(): OutgoingInvoice | null {
    return this.isCredit ? this.creditedInvoice : this.creditInvoice
  }

  /** The approval as it was invoiced: a credit reads it through the invoice it cancels. */
  public get snapshotId(): string | null {
    return this.productionStatementApprovalSnapshot ?? this.creditedInvoice?.snapshotId ?? null
  }

  public get isReverseCharge(): boolean {
    return this.vatRate === OutgoingInvoiceVatRates.REVERSE_CHARGE
  }

  public get hasGAccount(): boolean {
    return this.gAccountPercentage !== null && Number(this.gAccountPercentage) > 0
  }

  /** Mailed, or deliberately not mailed, or still underway: how the history line describes the invoice. */
  public get historyLabel(): string {
    if (this.sentAt) return `verzonden op ${this.sentAt.format("DD-MM-YYYY")}`
    if (this.isFailed) return "niet verzonden"
    if (this.isFinished) return "niet verzonden"
    return "wordt verzonden"
  }

  /** Mailed, or deliberately not mailed: nothing is pending for this invoice any more. */
  public get isFinished(): boolean {
    return (
      this.status === OutgoingInvoiceStatuses.SENT ||
      this.status === OutgoingInvoiceStatuses.NOT_SENT
    )
  }

  public get isFailed(): boolean {
    return this.status === OutgoingInvoiceStatuses.FAILED
  }

  /** Still being generated or mailed: shown in the "Verzenden" panel, not in a chip (PNTS-3227). */
  public get isInProgress(): boolean {
    return !this.isFinished && !this.isFailed
  }

  public get vatRateLabel(): string {
    return outgoingInvoiceVatRateLabel(this.vatRate)
  }

  public get statusLabel(): string {
    switch (this.status) {
      case OutgoingInvoiceStatuses.PENDING:
        return "In wachtrij"
      case OutgoingInvoiceStatuses.GENERATING:
        return "Genereren"
      case OutgoingInvoiceStatuses.SENDING:
        return "Verzenden"
      case OutgoingInvoiceStatuses.SENT:
        return "Verzonden"
      case OutgoingInvoiceStatuses.FAILED:
        return "Mislukt"
      case OutgoingInvoiceStatuses.NOT_SENT:
        return "Niet verzonden"
      default:
        return ""
    }
  }

  public getDisplayName(): string {
    return `${this.isCredit ? "Creditfactuur" : "Factuur"} ${this.number}`
  }

  public getFileRelation(): FileRelation | undefined {
    return this.fileBucket?.fileRelations[0]
  }
}
